import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, TrendingUp, Users, Building2 } from "lucide-react";
import { formatNumber, dec } from "@/lib/formatters";
import { AlderBlock } from "./AlderBlock";

const KommuneDemographics = ({ demographicsData, income, alder }) => {
  if (!demographicsData || !(demographicsData.total_population || demographicsData.median_income)) return null;
  // Single source of truth for income: latest SSB 06944/05854 rows (same as Husholdningsøkonomi), fallback to demographics snapshot
  const inc = {
    median_income: income?.median_income ?? demographicsData.median_income,
    median_income_person: income?.median_income_person ?? demographicsData.median_income_person,
    median_income_after_tax: income?.median_income_after_tax,
    year: income?.year ?? demographicsData.income_year,
  };
  return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Age Distribution */}
            {demographicsData.total_population > 0 && (
              <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#103766]" />
                    Befolkning
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* Gender distribution */}
                  {(demographicsData.population_male > 0 || demographicsData.population_female > 0) && (
                    <div className="mb-6">
                      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">
                        Kjønnsfordeling
                      </p>
                      <div className="flex items-center gap-2 h-6 rounded-full overflow-hidden bg-[#F6F8FB]">
                        <div 
                          className="h-full bg-[#0EA5E9] flex items-center justify-end pr-2"
                          style={{ 
                            width: `${demographicsData.total_population > 0 ? (demographicsData.population_male / demographicsData.total_population * 100) : 50}%` 
                          }}
                        >
                          <span className="text-xs text-white font-medium">
                            {demographicsData.total_population > 0 
                              ? dec((demographicsData.population_male / demographicsData.total_population * 100), 1) 
                              : 50}%
                          </span>
                        </div>
                        <div 
                          className="h-full bg-[#EC4899] flex items-center justify-start pl-2 flex-1"
                        >
                          <span className="text-xs text-white font-medium">
                            {demographicsData.total_population > 0 
                              ? dec((demographicsData.population_female / demographicsData.total_population * 100), 1) 
                              : 50}%
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-[#4B647F]">
                        <span>Menn: {formatNumber(demographicsData.population_male)}</span>
                        <span>Kvinner: {formatNumber(demographicsData.population_female)}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Age distribution */}
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-3">
                    Aldersfordeling
                  </p>
                  <div className="space-y-3">
                    {[
                      { label: "0-5 år", value: demographicsData.age_0_5, color: "#6366F1" },
                      { label: "6-12 år", value: demographicsData.age_6_12, color: "#8B5CF6" },
                      { label: "13-15 år", value: demographicsData.age_13_15, color: "#A855F7" },
                      { label: "16-19 år", value: demographicsData.age_16_19, color: "#D946EF" },
                      { label: "20-44 år", value: demographicsData.age_20_44, color: "#3F8A86" },
                      { label: "45-66 år", value: demographicsData.age_45_66, color: "#0EA5E9" },
                      { label: "67-79 år", value: demographicsData.age_67_79, color: "#F59E0B" },
                      { label: "80+ år", value: demographicsData.age_80_plus, color: "#EF4444" },
                    ].map((group) => {
                      const percentage = demographicsData.total_population > 0 
                        ? dec(((group.value || 0) / demographicsData.total_population * 100), 1)
                        : 0;
                      return (
                        <div key={group.label} className="flex items-center gap-3">
                          <span className="text-xs text-[#8AA4BF] w-16">{group.label}</span>
                          <div className="flex-1 h-4 bg-[#F6F8FB] rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ width: `${percentage}%`, backgroundColor: group.color }}
                            />
                          </div>
                          <span className="text-xs text-[#1F2A36] w-12 text-right">{percentage}%</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#8AA4BF] mt-4">
                    Totalt: {formatNumber(demographicsData.total_population)} innbyggjarar
                  </p>
                  <AlderBlock data={alder} />
                  <p className="text-xs text-[#8AA4BF] mt-2">
                    Kjelde:{" "}
                    <a href="https://www.ssb.no/statbank/table/07459" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                      SSB 07459 <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Income */}
            {(inc.median_income > 0 || inc.median_income_person > 0) && (
              <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#3F8A86]" />
                    Inntekt{inc.year ? ` ${inc.year}` : ""}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    {inc.median_income_person > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Median bruttoinntekt</p>
                        <p className="text-4xl font-light text-[#1F2A36]" data-testid="income-median-person">{formatNumber(inc.median_income_person)}</p>
                        <p className="text-sm text-[#8AA4BF] mt-1">kr per person 17 år og eldre</p>
                      </div>
                    )}
                    {inc.median_income > 0 && (
                      <div className={inc.median_income_person > 0 ? "pt-3 border-t border-[#E6E9F0]" : ""}>
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Median samla inntekt per hushald</p>
                        <p className={inc.median_income_person > 0 ? "text-xl font-light text-[#4B647F]" : "text-4xl font-light text-[#1F2A36]"} data-testid="income-median-household">
                          {formatNumber(inc.median_income)} kr
                        </p>
                        {inc.median_income_after_tax > 0 && (
                          <p className="text-sm text-[#4B647F]" data-testid="income-median-household-after-tax">Etter skatt: {formatNumber(inc.median_income_after_tax)} kr</p>
                        )}
                      </div>
                    )}
                    
                    {/* Average persons per household */}
                    {demographicsData.num_households > 0 && demographicsData.total_population > 0 && (
                      <div className="pt-3 border-t border-[#E6E9F0]">
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                          Gjennomsnittleg hushaldsstorleik
                        </p>
                        <p className="text-xl font-light text-[#4B647F]">
                          {dec((demographicsData.total_population / demographicsData.num_households), 2)} personar
                        </p>
                        <p className="text-xs text-[#8AA4BF] mt-1">
                          ({formatNumber(demographicsData.num_households)} hushald)
                        </p>
                      </div>
                    )}
                    
                    <p className="text-xs text-[#8AA4BF] mt-4">
                      Kjelde:{" "}
                      <a href="https://www.ssb.no/statbank/table/05854" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                        SSB 05854 <ExternalLink className="w-3 h-3" />
                      </a>
                      {" / "}
                      <a href="https://www.ssb.no/statbank/table/06944" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                        06944 <ExternalLink className="w-3 h-3" />
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Housing */}
            {(demographicsData.housing_owned || demographicsData.housing_rented || demographicsData.housing_cooperative) && (
              <Card className="bg-white border border-[#E6E9F0] rounded-lg">
                <CardHeader className="border-b border-[#E6E9F0] pb-4">
                  <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#103766]" />
                    Buforhold
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {(() => {
                    const totalHouseholds = (demographicsData.housing_owned || 0) + 
                                           (demographicsData.housing_rented || 0) + 
                                           (demographicsData.housing_cooperative || 0);
                    return (
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-[#1F2A36]">Sjølveigarar</span>
                            <span className="text-sm font-medium text-[#1F2A36]">
                              {totalHouseholds > 0 ? dec(((demographicsData.housing_owned || 0) / totalHouseholds * 100), 1) : 0}%
                            </span>
                          </div>
                          <div className="h-3 bg-[#F6F8FB] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#3F8A86] rounded-full"
                              style={{ width: `${totalHouseholds > 0 ? (demographicsData.housing_owned || 0) / totalHouseholds * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-[#1F2A36]">Borettslag/Aksje</span>
                            <span className="text-sm font-medium text-[#1F2A36]">
                              {totalHouseholds > 0 ? dec(((demographicsData.housing_cooperative || 0) / totalHouseholds * 100), 1) : 0}%
                            </span>
                          </div>
                          <div className="h-3 bg-[#F6F8FB] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#0EA5E9] rounded-full"
                              style={{ width: `${totalHouseholds > 0 ? (demographicsData.housing_cooperative || 0) / totalHouseholds * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-[#1F2A36]">Leigetakarar</span>
                            <span className="text-sm font-medium text-[#1F2A36]">
                              {totalHouseholds > 0 ? dec(((demographicsData.housing_rented || 0) / totalHouseholds * 100), 1) : 0}%
                            </span>
                          </div>
                          <div className="h-3 bg-[#F6F8FB] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#F59E0B] rounded-full"
                              style={{ width: `${totalHouseholds > 0 ? (demographicsData.housing_rented || 0) / totalHouseholds * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-[#8AA4BF] mt-4">
                          Kjelde:{" "}
                          <a href="https://www.ssb.no/statbank/table/11038" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                            SSB 11038 <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
  );
};

export default KommuneDemographics;
